/**
 * Ca quỹ — opening the till, and counting out at the end of the shift.
 *
 * Two API quirks are handled here rather than hidden:
 *
 *  1. There is NO `GET /v1/cash-sessions` list. The only way to discover an
 *     already-open till is the 409 you get when you try to open a second one,
 *     whose `details.cashRegisterSessionId` names it. So the id is cached in
 *     localStorage and the 409 is treated as a successful "resume", not an
 *     error.
 *  2. The close response's `totals` has no `cashMinor` field. Cash is derived
 *     as `expectedCashMinor` (the movement ledger), which is also what the
 *     variance is measured against.
 */

import { useState } from 'react';
import {
  Badge,
  Card,
  ErrorState,
  LoadingState,
  MoneyText,
  NumericKeypad,
  Textarea,
  keypadToMinor,
  useToast,
} from '@sonauto/ui';
import { isApiError } from '@sonauto/api-client';
import { useApi } from '../../auth/AuthProvider';
import { useAsync, errorMessage } from '../../lib/hooks';
import { saveCashSessionId } from '../../lib/storage';
import { hhmm, vnd } from '../../lib/format';
import type { CashSessionDetailResponse, CloseCashSessionResponse } from '../../lib/types';

export interface CashSessionScreenProps {
  readonly branchId: string;
  readonly cashSessionId: string | null;
  readonly onChange: (id: string | null) => void;
}

export function CashSessionScreen({ branchId, cashSessionId, onChange }: CashSessionScreenProps): React.JSX.Element {
  const api = useApi();
  const toast = useToast();

  const [floatText, setFloatText] = useState('');
  const [countedText, setCountedText] = useState('');
  const [note, setNote] = useState('');
  const [closing, setClosing] = useState<CloseCashSessionResponse | null>(null);
  const [busy, setBusy] = useState(false);

  const detail = useAsync<CashSessionDetailResponse>(
    (signal) => api.payments.cashSession(cashSessionId ?? '', { signal }),
    [cashSessionId],
    { enabled: cashSessionId !== null },
  );

  const open = async (): Promise<void> => {
    setBusy(true);
    try {
      const result = await api.payments.openCashSession({
        branchId,
        openingFloatMinor: keypadToMinor(floatText, 0),
      });
      saveCashSessionId(result.id);
      onChange(result.id);
      toast.success('Đã mở ca quỹ');
    } catch (cause: unknown) {
      // 409 CONFLICT means this cashier already has an open till — the only
      // discovery path the API offers. Resume it instead of showing an error.
      if (isApiError(cause) && cause.status === 409) {
        const existing = cause.details?.['cashRegisterSessionId'];
        if (typeof existing === 'string') {
          saveCashSessionId(existing);
          onChange(existing);
          toast.info('Đã khôi phục ca quỹ đang mở của bạn');
          return;
        }
      }
      toast.error('Không mở được ca quỹ', errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  const close = async (): Promise<void> => {
    if (cashSessionId === null) return;
    setBusy(true);
    try {
      const result = await api.payments.closeCashSession(cashSessionId, {
        countedCashMinor: keypadToMinor(countedText, 0),
        closingNote: note.trim() === '' ? null : note.trim(),
      });
      setClosing(result);
      saveCashSessionId(null);
      onChange(null);
      if (result.varianceBreached) {
        toast.warning(
          'Lệch quỹ vượt ngưỡng',
          `Lệch ${vnd(result.varianceMinor)} — ngưỡng cảnh báo ${vnd(result.thresholdMinor)}.`,
        );
      } else {
        toast.success('Đã đóng ca quỹ');
      }
    } catch (cause: unknown) {
      toast.error('Không đóng được ca quỹ', errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  if (cashSessionId === null) {
    return (
      <div className="mx-auto max-w-xl space-y-4 p-4">
        {closing !== null ? <VarianceCard result={closing} /> : null}
        <Card title="Mở ca quỹ" description="Nhập số tiền lẻ đầu ca trong ngăn kéo.">
          <NumericKeypad
            value={floatText}
            onChange={setFloatText}
            submitLabel="Mở ca quỹ"
            onSubmit={() => void open()}
            disabled={busy}
            quickValues={[
              { label: '200k', value: '200000' },
              { label: '500k', value: '500000' },
              { label: '1tr', value: '1000000' },
            ]}
            display={
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-fg-muted">Tiền đầu ca</span>
                <MoneyText amountMinor={keypadToMinor(floatText, 0)} className="text-2xl font-bold" />
              </div>
            }
          />
        </Card>
        <p className="text-flow text-center text-xs text-fg-subtle">
          Máy chủ chưa có endpoint liệt kê ca quỹ (<code className="font-mono">GET /api/v1/cash-sessions</code>), nên
          mã ca quỹ đang mở được lưu tại máy và khôi phục qua lỗi 409 khi mở trùng.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4">
      {detail.initialLoading ? <LoadingState label="Đang tải ca quỹ…" /> : null}
      {detail.error !== null && detail.data === null ? (
        <ErrorState message={detail.error.message} onRetry={detail.reload} />
      ) : null}

      {detail.data !== null ? (
        <>
          <Card
            title={`Ca quỹ ${detail.data.session.business_date}`}
            description={`Mở lúc ${hhmm(detail.data.session.opened_at)}`}
            actions={<Badge tone={detail.data.session.status === 'OPEN' ? 'success' : 'neutral'}>{detail.data.session.status}</Badge>}
          >
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <Field label="Tiền đầu ca" amount={detail.data.session.opening_float_minor} />
              <Field label="Tiền mặt dự kiến" amount={detail.data.expectedCashMinor} />
              <Field label="Tổng bán" amount={detail.data.session.total_sales_minor} />
              <Field label="Hoàn tiền" amount={detail.data.refunds.totalMinor} />
            </dl>

            <h3 className="mt-4 text-sm font-semibold text-fg">Theo phương thức</h3>
            <ul className="mt-1 space-y-1 text-sm">
              {detail.data.paymentsByMethod.map((row) => (
                <li key={row.method} className="flex justify-between">
                  <span className="text-fg-muted">
                    {row.method} ({row.count})
                  </span>
                  <MoneyText amountMinor={row.total} />
                </li>
              ))}
            </ul>
          </Card>

          <Card title="Đóng ca — kiểm đếm tiền mặt">
            <NumericKeypad
              value={countedText}
              onChange={setCountedText}
              submitLabel="Đóng ca quỹ"
              onSubmit={() => void close()}
              disabled={busy}
              display={
                <div className="space-y-1">
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm text-fg-muted">Tiền mặt đếm được</span>
                    <MoneyText amountMinor={keypadToMinor(countedText, 0)} className="text-2xl font-bold" />
                  </div>
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-fg-muted">Chênh lệch tạm tính</span>
                    <MoneyText
                      amountMinor={keypadToMinor(countedText, 0) - detail.data.expectedCashMinor}
                      signed
                      className="font-semibold"
                    />
                  </div>
                </div>
              }
            />
            <Textarea
              className="mt-3"
              rows={2}
              placeholder="Ghi chú cuối ca (nếu có lệch, giải thích ở đây)…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Card>
        </>
      ) : null}
    </div>
  );
}

function Field({ label, amount }: { readonly label: string; readonly amount: number }): React.JSX.Element {
  return (
    <div>
      <dt className="text-xs text-fg-subtle">{label}</dt>
      <dd>
        <MoneyText amountMinor={amount} className="text-base font-semibold" />
      </dd>
    </div>
  );
}

function VarianceCard({ result }: { readonly result: CloseCashSessionResponse }): React.JSX.Element {
  const short = result.varianceMinor < 0;
  return (
    <Card
      title="Kết quả đóng ca"
      className={result.varianceBreached ? 'ring-2 ring-danger-solid' : undefined}
      actions={
        <Badge tone={result.varianceBreached ? 'danger' : 'success'}>
          {result.varianceBreached ? 'Vượt ngưỡng' : 'Trong ngưỡng'}
        </Badge>
      }
    >
      <dl className="grid grid-cols-2 gap-3 text-sm">
        <Field label="Dự kiến" amount={result.expectedCashMinor} />
        <Field label="Đếm được" amount={result.countedCashMinor} />
        <div>
          <dt className="text-xs text-fg-subtle">Chênh lệch ({short ? 'thiếu' : 'thừa'})</dt>
          <dd>
            <MoneyText amountMinor={result.varianceMinor} signed className="text-xl font-bold" />
          </dd>
        </div>
        <Field label="Ngưỡng cảnh báo" amount={result.thresholdMinor} />
      </dl>
    </Card>
  );
}
